import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import * as THREE from 'three';

@Component({
  selector: 'app-three-background',
  template: '<div #rendererContainer class="three-container"></div>',
  styles: [`
    .three-container {
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 0;
      background: transparent;
      overflow: hidden;
    }
  `]
})
export class ThreeBackgroundComponent implements AfterViewInit, OnDestroy {
  @ViewChild('rendererContainer') rendererContainer: ElementRef;

  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private animationId: number;
  private clock = new THREE.Clock();

  private scrollY = 0;
  private targetScrollY = 0;

  // Terrain mesh (the flowing data-wave surface)
  private terrainMesh: THREE.Mesh;
  private terrainGeo: THREE.PlaneGeometry;
  private terrainSegX = 180;
  private terrainSegZ = 90;

  // Network graph nodes + lines
  private networkNodes: THREE.Points;
  private networkLines: THREE.LineSegments;
  private nodeBasePositions: Float32Array;

  // Subtle ambient particles (dust, not flashy)
  private dustParticles: THREE.Points;

  // Camera path — office tour: outside → lobby → workspace → meeting → datacenter
  private cameraPath = [
    // Hero — see office exterior from elevated angle (bottom-left)
    { pos: { x: 25, y: 30, z: 60 }, look: { x: -20, y: 3, z: -40 } },
    // Approach office entrance
    { pos: { x: 0, y: 12, z: 10 }, look: { x: 0, y: 5, z: -50 } },
    // Enter lobby
    { pos: { x: 0, y: 8, z: -30 }, look: { x: 0, y: 5, z: -100 } },
    // Walk into open workspace
    { pos: { x: 3, y: 8, z: -110 }, look: { x: 0, y: 5, z: -180 } },
    // Through desks area
    { pos: { x: -3, y: 8, z: -190 }, look: { x: 2, y: 5, z: -260 } },
    // Pass collaborative zone
    { pos: { x: 4, y: 8, z: -270 }, look: { x: -2, y: 5, z: -340 } },
    // Approach meeting room
    { pos: { x: -2, y: 8, z: -350 }, look: { x: 0, y: 5, z: -420 } },
    // Inside meeting room area
    { pos: { x: 2, y: 8, z: -430 }, look: { x: -1, y: 5, z: -500 } },
    // Transition corridor
    { pos: { x: -3, y: 8, z: -510 }, look: { x: 1, y: 5, z: -580 } },
    // Enter data center
    { pos: { x: 0, y: 8, z: -590 }, look: { x: 0, y: 5, z: -660 } },
    // Inside data center — server racks
    { pos: { x: 2, y: 8, z: -670 }, look: { x: -1, y: 5, z: -740 } },
    // Final — deep in data center
    { pos: { x: 0, y: 10, z: -750 }, look: { x: 0, y: 4, z: -830 } },
  ];

  private scrollHandler: () => void;
  private resizeHandler: () => void;

  constructor(private ngZone: NgZone) { }

  ngAfterViewInit() {

    try {
      this.initRenderer();
      this.buildScene();
      this.startLoop();
      this.scrollHandler = () => { this.targetScrollY = window.scrollY; };
      this.resizeHandler = () => this.onResize();
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      window.addEventListener('resize', this.resizeHandler);

    } catch (e) {
      console.error('THREE ERROR:', e);
    }


  }

  /* ═══════════════════════════════════════════════════════
     RENDERER
     ═══════════════════════════════════════════════════════ */
  private initRenderer() {
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x060b18, 0.0012);

    this.camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.5, 2000);
    this.camera.position.set(25, 30, 60);

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.9;
    this.rendererContainer.nativeElement.appendChild(this.renderer.domElement);
  }

  /* ═══════════════════════════════════════════════════════
     SCENE ASSEMBLY
     ═══════════════════════════════════════════════════════ */
  private buildScene() {
    this.addLights();
    this.createTerrain();
    this.createCity();
    this.createNetworkGraph();
    this.createDust();
  }

  /* ═══════════════════════════════════════════════════════
     LIGHTS — soft, directional, no point-light sparkles
     ═══════════════════════════════════════════════════════ */
  private addLights() {
    // Soft ambient
    this.scene.add(new THREE.AmbientLight(0x0a1830, 0.6));

    // Key light — cool cyan tint, very soft
    const key = new THREE.DirectionalLight(0x1a6080, 0.5);
    key.position.set(40, 80, 60);
    this.scene.add(key);

    // Fill — warm accent from below
    const fill = new THREE.DirectionalLight(0x0a3050, 0.25);
    fill.position.set(-30, 20, -40);
    this.scene.add(fill);

    // Hemisphere for natural gradation
    this.scene.add(new THREE.HemisphereLight(0x1a3a5c, 0x060b18, 0.35));
  }

  /* ═══════════════════════════════════════════════════════
     TERRAIN — the flowing data-landscape surface
     A wireframe plane with sine-wave displacement that
     animates gently. This replaces the "videogame" look
     with an elegant topographic data-viz aesthetic.
     ═══════════════════════════════════════════════════════ */
  private createTerrain() {
    const width = 300;
    const depth = 1500;
    this.terrainGeo = new THREE.PlaneGeometry(width, depth, this.terrainSegX, this.terrainSegZ);
    this.terrainGeo.rotateX(-Math.PI / 2);

    // Displace vertices to create initial wave shape
    const pos = this.terrainGeo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const y = this.terrainHeight(x, z, 0);
      pos.setY(i, y);
    }
    pos.needsUpdate = true;
    this.terrainGeo.computeVertexNormals();

    // Material — transparent wireframe with a subtle cyan tint
    const mat = new THREE.MeshBasicMaterial({
      color: 0x0e4a6a,
      wireframe: true,
      transparent: true,
      opacity: 0.18,
      depthWrite: false,
    });

    this.terrainMesh = new THREE.Mesh(this.terrainGeo, mat);
    this.terrainMesh.position.set(0, -8, -650);
    this.scene.add(this.terrainMesh);

    // Secondary terrain layer — even more subtle, offset
    const mat2 = new THREE.MeshBasicMaterial({
      color: 0x08354a,
      wireframe: true,
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
    });
    const terrain2 = new THREE.Mesh(this.terrainGeo.clone(), mat2);
    terrain2.position.set(0, -14, -650);
    terrain2.scale.set(1.3, 0.6, 1);
    this.scene.add(terrain2);
  }

  // Procedural height function — layered sine waves
  private terrainHeight(x: number, z: number, t: number): number {
    const s1 = Math.sin(x * 0.02 + t * 0.3) * 6;
    const s2 = Math.sin(z * 0.008 + t * 0.15) * 10;
    const s3 = Math.sin((x + z) * 0.015 + t * 0.2) * 4;
    const s4 = Math.sin(x * 0.05 + z * 0.02 + t * 0.4) * 2;
    return s1 + s2 + s3 + s4;
  }

  /* ═══════════════════════════════════════════════════════
     OFFICE — modern software office interior
     Lobby → Open workspace → Meeting rooms → Data center
     ═══════════════════════════════════════════════════════ */
  private createCity() {
    const office = new THREE.Group();

    // --- MATERIALS ---
    const floorMat = new THREE.MeshStandardMaterial({ color: 0x0e1520, roughness: 0.9, metalness: 0.1 });
    const wallMat = new THREE.MeshStandardMaterial({ color: 0x111a28, roughness: 0.85, metalness: 0.1 });
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: 0x1a3550, transparent: true, opacity: 0.15,
      roughness: 0.05, metalness: 0.05, side: THREE.DoubleSide
    });
    const deskMat = new THREE.MeshStandardMaterial({ color: 0x1a2235, roughness: 0.6, metalness: 0.3 });
    const screenMat = new THREE.MeshBasicMaterial({ color: 0x15404f, transparent: true, opacity: 0.7 });
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x0c1018, roughness: 0.4, metalness: 0.6 });
    const accentMat = new THREE.MeshBasicMaterial({ color: 0x14404f, transparent: true, opacity: 0.5 });
    const rackMat = new THREE.MeshStandardMaterial({ color: 0x0a0f18, roughness: 0.5, metalness: 0.5 });
    const ledMat = new THREE.MeshBasicMaterial({ color: 0x1a5565, transparent: true, opacity: 0.6 });

    const officeW = 40; // total office width
    const halfW = officeW / 2;

    // --- CONTINUOUS FLOOR ---
    const floorGeo = new THREE.PlaneGeometry(officeW, 900, 20, 45);
    floorGeo.rotateX(-Math.PI / 2);
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.position.set(0, 0, -400);
    office.add(floor);

    // Subtle floor grid overlay
    const gridGeo = new THREE.PlaneGeometry(officeW, 900, 40, 90);
    gridGeo.rotateX(-Math.PI / 2);
    const grid = new THREE.Mesh(gridGeo, new THREE.MeshBasicMaterial({
      color: 0x152030, wireframe: true, transparent: true, opacity: 0.06
    }));
    grid.position.set(0, 0.05, -400);
    office.add(grid);

    // --- WALLS (left & right running the entire length) ---
    const wallH = 14;
    const wallGeo = new THREE.BoxGeometry(0.5, wallH, 900);
    const lWall = new THREE.Mesh(wallGeo, wallMat);
    lWall.position.set(-halfW, wallH / 2, -400);
    office.add(lWall);
    const rWall = new THREE.Mesh(wallGeo.clone(), wallMat.clone());
    rWall.position.set(halfW, wallH / 2, -400);
    office.add(rWall);

    // --- CEILING ---
    const ceilGeo = new THREE.PlaneGeometry(officeW, 900);
    ceilGeo.rotateX(Math.PI / 2);
    const ceil = new THREE.Mesh(ceilGeo, new THREE.MeshStandardMaterial({
      color: 0x0c1420, roughness: 0.95, metalness: 0.05
    }));
    ceil.position.set(0, wallH, -400);
    office.add(ceil);

    // --- CEILING LIGHT STRIPS (subtle accent lines) ---
    for (let z = 10; z > -850; z -= 35) {
      const strip = new THREE.Mesh(
        new THREE.BoxGeometry(officeW * 0.6, 0.15, 0.8),
        accentMat.clone()
      );
      strip.position.set(0, wallH - 0.1, z);
      office.add(strip);
    }

    // --- GLASS FACADE (front of office, visible from hero) ---
    const facadeGeo = new THREE.PlaneGeometry(officeW, wallH);
    const facade = new THREE.Mesh(facadeGeo, glassMat.clone());
    facade.position.set(0, wallH / 2, 5);
    office.add(facade);

    // Facade frame lines
    for (let i = -3; i <= 3; i++) {
      const vLine = new THREE.Mesh(new THREE.BoxGeometry(0.15, wallH, 0.15), frameMat);
      vLine.position.set(i * (officeW / 7), wallH / 2, 5);
      office.add(vLine);
    }

    // ═══ ZONE 1: LOBBY (z: 0 to -80) ═══
    // Reception desk
    const recepGeo = new THREE.BoxGeometry(16, 4.5, 3);
    const recep = new THREE.Mesh(recepGeo, deskMat);
    recep.position.set(0, 2.25, -25);
    office.add(recep);
    // Reception screen
    const recScr = new THREE.Mesh(new THREE.BoxGeometry(6, 3, 0.2), screenMat);
    recScr.position.set(0, 5.5, -25);
    office.add(recScr);

    // ═══ ZONE 2: OPEN WORKSPACE (z: -80 to -300) ═══
    const deskPositions = [
      // Pod 1
      { x: -12, z: -100 }, { x: -12, z: -108 },
      { x: 12, z: -100 }, { x: 12, z: -108 },
      // Pod 2
      { x: -12, z: -150 }, { x: -12, z: -158 },
      { x: 12, z: -150 }, { x: 12, z: -158 },
      // Pod 3
      { x: -12, z: -200 }, { x: -12, z: -208 },
      { x: 12, z: -200 }, { x: 12, z: -208 },
      // Pod 4
      { x: -12, z: -250 }, { x: -12, z: -258 },
      { x: 12, z: -250 }, { x: 12, z: -258 },
    ];
    deskPositions.forEach(p => {
      // Desk surface
      const desk = new THREE.Mesh(new THREE.BoxGeometry(8, 0.4, 5), deskMat);
      desk.position.set(p.x, 3.5, p.z);
      office.add(desk);
      // Desk legs
      [[-3.5, -2], [3.5, -2], [-3.5, 2], [3.5, 2]].forEach(([lx, lz]) => {
        const leg = new THREE.Mesh(new THREE.BoxGeometry(0.2, 3.5, 0.2), frameMat);
        leg.position.set(p.x + lx, 1.75, p.z + lz);
        office.add(leg);
      });
      // Monitor
      const mon = new THREE.Mesh(new THREE.BoxGeometry(4, 2.5, 0.15), screenMat);
      mon.position.set(p.x, 5.5, p.z - 1.5);
      office.add(mon);
      // Monitor stand
      const stand = new THREE.Mesh(new THREE.BoxGeometry(0.3, 1.5, 0.3), frameMat);
      stand.position.set(p.x, 4.5, p.z - 1.5);
      office.add(stand);
    });

    // Divider partitions between desk pods
    [-130, -180, -230].forEach(z => {
      const divider = new THREE.Mesh(new THREE.BoxGeometry(officeW * 0.5, 5, 0.2), glassMat.clone());
      divider.position.set(0, 5, z);
      office.add(divider);
    });

    // ═══ ZONE 3: MEETING ROOMS (z: -320 to -500) ═══
    // Glass partitions creating rooms
    [-320, -420].forEach((z, ri) => {
      // Back wall of meeting room
      const mWall = new THREE.Mesh(new THREE.BoxGeometry(18, wallH, 0.4), wallMat.clone());
      mWall.position.set(ri % 2 === 0 ? -8 : 8, wallH / 2, z);
      office.add(mWall);
      // Glass side
      const mGlass = new THREE.Mesh(new THREE.PlaneGeometry(0.4, wallH), glassMat.clone());
      mGlass.rotation.y = Math.PI / 2;
      mGlass.position.set(ri % 2 === 0 ? 1 : -1, wallH / 2, z + 20);
      office.add(mGlass);
      // Conference table
      const table = new THREE.Mesh(new THREE.BoxGeometry(10, 0.5, 5), deskMat);
      table.position.set(ri % 2 === 0 ? -8 : 8, 3.5, z + 20);
      office.add(table);
      // Whiteboard/screen
      const wb = new THREE.Mesh(new THREE.BoxGeometry(8, 4, 0.15), screenMat);
      wb.position.set(ri % 2 === 0 ? -8 : 8, 7, z + 2);
      office.add(wb);
    });

    // ═══ ZONE 4: DATA CENTER (z: -540 to -850) ═══
    // Heavy door frame
    const doorFrame = new THREE.Mesh(new THREE.BoxGeometry(officeW, wallH, 1), wallMat.clone());
    doorFrame.position.set(0, wallH / 2, -540);
    office.add(doorFrame);
    // Door opening (cut-out effect — glass)
    const doorGlass = new THREE.Mesh(new THREE.PlaneGeometry(8, 10), glassMat.clone());
    doorGlass.position.set(0, 5, -540.1);
    office.add(doorGlass);

    // Server racks — two rows
    for (let z = -570; z > -830; z -= 22) {
      [-14, -8, 8, 14].forEach(x => {
        const rack = new THREE.Mesh(new THREE.BoxGeometry(4, 10, 2.5), rackMat);
        rack.position.set(x, 5, z);
        office.add(rack);
        // LED status indicators on racks
        for (let ly = 1; ly < 9; ly += 1.5) {
          if (Math.random() > 0.4) {
            const led = new THREE.Mesh(new THREE.BoxGeometry(3, 0.2, 0.1), ledMat.clone());
            led.position.set(x, ly, z + 1.3);
            office.add(led);
          }
        }
      });
    }

    // Raised floor grid in data center
    const dcFloor = new THREE.Mesh(
      new THREE.PlaneGeometry(officeW, 300, 20, 15),
      new THREE.MeshBasicMaterial({ color: 0x0c1825, wireframe: true, transparent: true, opacity: 0.1 })
    );
    dcFloor.rotation.x = -Math.PI / 2;
    dcFloor.position.set(0, 0.1, -690);
    office.add(dcFloor);

    this.scene.add(office);
  }

  /* ═══════════════════════════════════════════════════════
     NETWORK GRAPH — elegant node-and-edge visualization
     Soft glowing nodes connected by thin lines,
     like a professional data-network diagram.
     ═══════════════════════════════════════════════════════ */
  private createNetworkGraph() {
    const nodeCount = 600;
    const positions = new Float32Array(nodeCount * 3);
    const colors = new Float32Array(nodeCount * 3);
    this.nodeBasePositions = new Float32Array(nodeCount * 3);

    // Palette: muted cyan, teal, slate blue
    const palette = [
      new THREE.Color(0x1a8aaa), // teal
      new THREE.Color(0x2070a0), // steel blue
      new THREE.Color(0x0e5a78), // dark cyan
      new THREE.Color(0x30a0b0), // lighter teal
    ];

    for (let i = 0; i < nodeCount; i++) {
      const i3 = i * 3;
      const x = (Math.random() - 0.5) * 250;
      const z = Math.random() * -1400;
      const y = this.terrainHeight(x, z + 650, 0) + 3 + Math.random() * 18;

      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;
      this.nodeBasePositions[i3] = x;
      this.nodeBasePositions[i3 + 1] = y;
      this.nodeBasePositions[i3 + 2] = z;

      const c = palette[Math.floor(Math.random() * palette.length)];
      colors[i3] = c.r;
      colors[i3 + 1] = c.g;
      colors[i3 + 2] = c.b;
    }

    const nodeGeo = new THREE.BufferGeometry();
    nodeGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    nodeGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    this.networkNodes = new THREE.Points(nodeGeo, new THREE.PointsMaterial({
      size: 1.6,
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      sizeAttenuation: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    }));
    this.scene.add(this.networkNodes);

    // Build edges — connect nearby nodes
    const edgePositions: number[] = [];
    const edgeColors: number[] = [];
    const threshold = 22; // max connection distance
    const thresholdSq = threshold * threshold;

    for (let i = 0; i < nodeCount; i++) {
      const i3 = i * 3;
      let connections = 0;
      for (let j = i + 1; j < nodeCount && connections < 3; j++) {
        const j3 = j * 3;
        const dx = positions[i3] - positions[j3];
        const dy = positions[i3 + 1] - positions[j3 + 1];
        const dz = positions[i3 + 2] - positions[j3 + 2];
        const distSq = dx * dx + dy * dy + dz * dz;
        if (distSq < thresholdSq) {
          edgePositions.push(
            positions[i3], positions[i3 + 1], positions[i3 + 2],
            positions[j3], positions[j3 + 1], positions[j3 + 2]
          );
          const a = 0.06;
          edgeColors.push(0.1, 0.35, 0.5, a, a, a, 0.1, 0.35, 0.5, a, a, a);
          connections++;
        }
      }
    }

    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(edgePositions, 3));

    this.networkLines = new THREE.LineSegments(lineGeo, new THREE.LineBasicMaterial({
      color: 0x1a6080,
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
    }));
    this.scene.add(this.networkLines);
  }

  /* ═══════════════════════════════════════════════════════
     DUST — very subtle atmospheric particles
     ═══════════════════════════════════════════════════════ */
  private createDust() {
    const count = 800;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 300;
      pos[i * 3 + 1] = Math.random() * 60;
      pos[i * 3 + 2] = Math.random() * -1400;
      // Muted blue-grey
      col[i * 3] = 0.15 + Math.random() * 0.1;
      col[i * 3 + 1] = 0.25 + Math.random() * 0.15;
      col[i * 3 + 2] = 0.35 + Math.random() * 0.15;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));

    this.dustParticles = new THREE.Points(geo, new THREE.PointsMaterial({
      size: 0.8,
      vertexColors: true,
      transparent: true,
      opacity: 0.25,
      sizeAttenuation: true,
      depthWrite: false,
    }));
    this.scene.add(this.dustParticles);
  }

  /* ═══════════════════════════════════════════════════════
     ANIMATION LOOP
     ═══════════════════════════════════════════════════════ */
  private startLoop() {
    this.ngZone.runOutsideAngular(() => {
      const tick = () => {
        this.animationId = requestAnimationFrame(tick);
        const t = this.clock.getElapsedTime();

        this.scrollY += (this.targetScrollY - this.scrollY) * 0.06;

        this.animateTerrain(t);
        this.animateNodes(t);
        this.animateDust(t);
        this.updateCamera(t);

        this.renderer.render(this.scene, this.camera);
      };
      tick();
    });
  }

  /* ═══════════════════════════════════════════════════════
     TERRAIN ANIMATION — gentle wave motion
     ═══════════════════════════════════════════════════════ */
  private animateTerrain(t: number) {
    const pos = this.terrainGeo.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      pos.setY(i, this.terrainHeight(x, z, t));
    }
    pos.needsUpdate = true;
  }

  /* ═══════════════════════════════════════════════════════
     NODE ANIMATION — slow gentle float
     ═══════════════════════════════════════════════════════ */
  private animateNodes(t: number) {
    const pos = this.networkNodes.geometry.attributes.position.array as Float32Array;
    const base = this.nodeBasePositions;
    for (let i = 0; i < pos.length; i += 3) {
      pos[i + 1] = base[i + 1] + Math.sin(t * 0.3 + base[i] * 0.02) * 1.5;
    }
    this.networkNodes.geometry.attributes.position.needsUpdate = true;
  }

  /* ═══════════════════════════════════════════════════════
     DUST ANIMATION — barely perceptible drift
     ═══════════════════════════════════════════════════════ */
  private animateDust(t: number) {
    const pos = this.dustParticles.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < pos.length; i += 3) {
      pos[i + 1] += Math.sin(t * 0.2 + pos[i] * 0.01) * 0.008;
    }
    this.dustParticles.geometry.attributes.position.needsUpdate = true;
  }

  /* ═══════════════════════════════════════════════════════
     CAMERA — smooth scroll-driven journey
     ═══════════════════════════════════════════════════════ */
  private updateCamera(t: number) {
    const maxScroll = Math.max(document.body.scrollHeight - window.innerHeight, 1);
    const frac = Math.min(Math.max(this.scrollY / maxScroll, 0), 1);

    const len = this.cameraPath.length - 1;
    const progress = frac * len;
    const idx = Math.min(Math.floor(progress), len - 1);
    const f = progress - idx;
    const ease = 1 - Math.pow(1 - f, 3);

    const p1 = this.cameraPath[idx];
    const p2 = this.cameraPath[idx + 1] || p1;

    // Very subtle sway — professional, not distracting
    const swayX = Math.sin(t * 0.15) * 1.2;
    const swayY = Math.cos(t * 0.1) * 0.8;

    this.camera.position.x = THREE.MathUtils.lerp(p1.pos.x, p2.pos.x, ease) + swayX;
    this.camera.position.y = THREE.MathUtils.lerp(p1.pos.y, p2.pos.y, ease) + swayY;
    this.camera.position.z = THREE.MathUtils.lerp(p1.pos.z, p2.pos.z, ease);

    const lx = THREE.MathUtils.lerp(p1.look.x, p2.look.x, ease);
    const ly = THREE.MathUtils.lerp(p1.look.y, p2.look.y, ease);
    const lz = THREE.MathUtils.lerp(p1.look.z, p2.look.z, ease);
    this.camera.lookAt(lx, ly, lz);
  }

  /* ═══════════════════════════════════════════════════════
     RESIZE
     ═══════════════════════════════════════════════════════ */
  private onResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  ngOnDestroy() {
    if (this.animationId) cancelAnimationFrame(this.animationId);
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
    if (this.renderer) this.renderer.dispose();
  }
}